import React, { useState, useMemo } from 'react'
import { IoRefresh, IoInformationCircle, IoWarning, IoAlertCircle, IoCheckmarkCircle, IoFlash, IoStatsChart, IoTime, IoPersonOutline, IoShieldOutline, IoTrophy, IoFootball } from 'react-icons/io5'
import { useLeague } from '../context/LeagueContext'
import { fmtKickoff, fmtDateHeader, getDateKey } from '../utils'
import { SkeletonCard } from './Skeleton'
import useSWR from 'swr'
import { apiGet } from '../api'
import { useFeatureTracking, FeatureNames } from '../hooks/useActivityTracking'

// Types matching backend schemas
interface FactorContribution {
  factor: string
  category: string
  impact: number
  impact_label: string
  detail: string
  data_quality: number
}

interface RiskFlag {
  flag: string
  severity: 'info' | 'warning' | 'critical'
  affected?: string
}

interface MatchCardPredictionSummary {
  fixture_id: number
  expected_total: number
  home_expected: number
  away_expected: number
  over_3_5_prob: number
  confidence_level: 'high' | 'moderate' | 'low' | 'speculative'
  confidence_score: number
  top_factor: string
  risk_flag_count: number
}

interface FixtureWithPrediction {
  fixture_id: number
  api_fixture_id: number
  kickoff_utc: string
  status: string
  round: string | null
  venue: string | null
  home_team_id: number
  home_team_name: string
  away_team_id: number
  away_team_name: string
  referee_name: string | null
  referee_strictness: number | null
  is_derby: boolean
  derby_intensity: string | null
  is_relegation_battle: boolean
  is_title_race: boolean
  prediction: MatchCardPredictionSummary | null
}

interface AIPredictionsPageResponse {
  fixtures: FixtureWithPrediction[]
  league_id: number
  league_name: string
  league_average_cards: number
  total_fixtures: number
  fixtures_with_predictions: number
  highest_expected: FixtureWithPrediction | null
  most_confident: FixtureWithPrediction | null
  derby_fixtures: number[]
  generated_at: string
}

// Detailed prediction for modal/expanded view
interface LineProbability {
  line: number
  over_prob: number
  under_prob: number
}

interface TeamCardPrediction {
  team_id: number
  team_name: string
  expected_cards: number
  lines: LineProbability[]
  key_factors: string[]
}

interface TimeBasedPrediction {
  first_half: { expected_cards: number; over_1_5_prob?: number }
  second_half: { expected_cards: number; over_2_5_prob?: number }
  first_card_timing: { under_15_mins: number; under_30_mins: number; under_45_mins?: number }
}

interface DataQualityReport {
  referee_data_quality: number
  referee_matches: number
  team_data_quality: number
  team_matches: number
  h2h_data_quality: number
  h2h_matches: number
  player_data_quality: number
  overall_quality: number
  fallbacks_used: string[]
}

interface MatchCardPrediction {
  fixture_id: number
  prediction_type: string
  expected_total_cards: number
  expected_total_yellows: number
  expected_total_reds: number | null
  total_lines: LineProbability[]
  home_team: TeamCardPrediction
  away_team: TeamCardPrediction
  time_based: TimeBasedPrediction | null
  red_card_prob: number | null
  confidence_score: number
  confidence_level: string
  explanation: string
  key_factors: FactorContribution[]
  risk_flags: RiskFlag[]
  data_quality: DataQualityReport
  generated_at: string
}

// API fetcher using apiGet for correct base URL
const fetcher = <T,>(url: string): Promise<T> => apiGet<T>(url)

// Confidence badge component
function ConfidenceBadge({ level, score }: { level: string; score: number }) {
  const colors: Record<string, string> = {
    high: 'var(--green)',
    moderate: 'var(--yellow)',
    low: 'var(--orange)',
    speculative: 'var(--red)',
  }
  const labels: Record<string, string> = {
    high: 'High',
    moderate: 'Moderate',
    low: 'Low',
    speculative: 'Speculative',
  }
  const icons: Record<string, React.ReactNode> = {
    high: <IoCheckmarkCircle size={14} />,
    moderate: <IoInformationCircle size={14} />,
    low: <IoWarning size={14} />,
    speculative: <IoAlertCircle size={14} />,
  }

  return (
    <span
      className="confidence-badge"
      style={{ '--badge-color': colors[level] || 'var(--muted)' } as React.CSSProperties}
      title={`${labels[level]} confidence (${(score * 100).toFixed(0)}%)`}
    >
      {icons[level]}
      <span>{labels[level]}</span>
    </span>
  )
}

// Expected cards visual
function ExpectedCardsBar({ expected, max = 8 }: { expected: number; max?: number }) {
  const pct = Math.min(100, (expected / max) * 100)
  const color = expected >= 5.5 ? 'var(--red)' : expected >= 4 ? 'var(--yellow)' : 'var(--green)'

  return (
    <div className="expected-cards-bar">
      <div
        className="expected-cards-fill"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
      <span className="expected-cards-value">{expected.toFixed(1)}</span>
    </div>
  )
}

// Classification badges
function ClassificationBadges({ fixture }: { fixture: FixtureWithPrediction }) {
  return (
    <div className="classification-badges">
      {fixture.is_derby && (
        <span className="badge badge-derby" title={`${fixture.derby_intensity || 'Regional'} derby`}>
          <IoFlash size={12} />
          {fixture.derby_intensity === 'fierce' ? 'Fierce Derby' : 'Derby'}
        </span>
      )}
      {fixture.is_relegation_battle && (
        <span className="badge badge-relegation" title="Relegation battle">
          <IoShieldOutline size={12} />
          Relegation
        </span>
      )}
      {fixture.is_title_race && (
        <span className="badge badge-title" title="Title race match">
          <IoTrophy size={12} />
          Title Race
        </span>
      )}
    </div>
  )
}

// Referee strictness indicator
function RefereeStrictness({ name, strictness }: { name: string; strictness: number | null }) {
  if (!strictness) return null

  const label = strictness >= 1.15 ? 'Strict' : strictness <= 0.85 ? 'Lenient' : 'Average'
  const color = strictness >= 1.15 ? 'var(--red)' : strictness <= 0.85 ? 'var(--green)' : 'var(--muted)'
  const pctDiff = ((strictness - 1) * 100).toFixed(0)

  return (
    <div className="referee-info">
      <IoPersonOutline size={14} />
      <span className="referee-name">{name}</span>
      <span className="referee-strictness" style={{ color }}>
        ({label}, {pctDiff}%)
      </span>
    </div>
  )
}

// Single fixture prediction card
function PredictionCard({
  fixture,
  isExpanded,
  onToggle,
  leagueAvg,
}: {
  fixture: FixtureWithPrediction
  isExpanded: boolean
  onToggle: () => void
  leagueAvg: number
}) {
  const prediction = fixture.prediction

  // Load detailed prediction when expanded
  const { data: detailed, isLoading: detailLoading } = useSWR<MatchCardPrediction>(
    isExpanded ? `/card-predictions/fixture/${fixture.fixture_id}` : null,
    fetcher
  )

  const expectedDiff = prediction ? prediction.expected_total - leagueAvg : 0
  const expectedLabel = expectedDiff >= 1 ? 'High' : expectedDiff <= -1 ? 'Low' : 'Average'

  return (
    <div className={`prediction-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="prediction-card-header" onClick={onToggle}>
        {/* Left: Teams */}
        <div className="prediction-teams">
          <div className="prediction-teams-names">
            <span className="team-home">{fixture.home_team_name}</span>
            <span className="vs">vs</span>
            <span className="team-away">{fixture.away_team_name}</span>
          </div>
          <div className="prediction-meta">
            <span className="kickoff">{fmtKickoff(fixture.kickoff_utc)}</span>
            {fixture.venue && <span className="venue">{fixture.venue}</span>}
          </div>
          <ClassificationBadges fixture={fixture} />
        </div>

        {/* Right: Prediction summary */}
        <div className="prediction-summary">
          {prediction ? (
            <>
              <div className="expected-cards-container">
                <div className="expected-label">{expectedLabel} Card Expectation</div>
                <ExpectedCardsBar expected={prediction.expected_total} />
              </div>
              <div className="prediction-quick-stats">
                <span className="stat">
                  <span className="stat-label">Over 3.5</span>
                  <span className="stat-value">{(prediction.over_3_5_prob * 100).toFixed(0)}%</span>
                </span>
                <ConfidenceBadge level={prediction.confidence_level} score={prediction.confidence_score} />
              </div>
            </>
          ) : (
            <div className="no-prediction">
              <IoInformationCircle size={16} />
              <span>Computing...</span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="prediction-card-detail">
          {detailLoading ? (
            <div className="detail-loading">Loading detailed prediction...</div>
          ) : detailed ? (
            <>
              {/* Referee info */}
              {fixture.referee_name && (
                <RefereeStrictness name={fixture.referee_name} strictness={fixture.referee_strictness} />
              )}

              {/* Explanation */}
              <div className="prediction-explanation">
                <IoStatsChart size={16} />
                <p>{detailed.explanation}</p>
              </div>

              {/* Team breakdown */}
              <div className="team-breakdown">
                <div className="team-column">
                  <div className="team-header">{detailed.home_team.team_name}</div>
                  <div className="team-expected">{detailed.home_team.expected_cards.toFixed(1)} cards</div>
                  <div className="team-lines">
                    {detailed.home_team.lines.map(line => (
                      <div key={line.line} className="line-row">
                        <span>O{line.line}</span>
                        <span className="line-prob">{(line.over_prob * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="team-column">
                  <div className="team-header">{detailed.away_team.team_name}</div>
                  <div className="team-expected">{detailed.away_team.expected_cards.toFixed(1)} cards</div>
                  <div className="team-lines">
                    {detailed.away_team.lines.map(line => (
                      <div key={line.line} className="line-row">
                        <span>O{line.line}</span>
                        <span className="line-prob">{(line.over_prob * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Total lines */}
              <div className="total-lines">
                <div className="total-lines-header">Match Total Cards</div>
                <div className="lines-grid">
                  {detailed.total_lines.map(line => (
                    <div key={line.line} className="line-chip">
                      <span className="line-label">O{line.line}</span>
                      <span className="line-value">{(line.over_prob * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Time-based */}
              {detailed.time_based && (
                <div className="time-based">
                  <div className="time-header">
                    <IoTime size={14} />
                    Time-Based Predictions
                  </div>
                  <div className="time-grid">
                    <div className="time-period">
                      <span className="period-label">First Half</span>
                      <span className="period-value">{detailed.time_based.first_half.expected_cards.toFixed(1)} cards</span>
                    </div>
                    <div className="time-period">
                      <span className="period-label">Second Half</span>
                      <span className="period-value">{detailed.time_based.second_half.expected_cards.toFixed(1)} cards</span>
                    </div>
                    <div className="time-period">
                      <span className="period-label">Card by 15'</span>
                      <span className="period-value">{(detailed.time_based.first_card_timing.under_15_mins * 100).toFixed(0)}%</span>
                    </div>
                    <div className="time-period">
                      <span className="period-label">Card by 30'</span>
                      <span className="period-value">{(detailed.time_based.first_card_timing.under_30_mins * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Key factors */}
              <div className="key-factors">
                <div className="factors-header">Key Factors</div>
                <div className="factors-list">
                  {detailed.key_factors.slice(0, 5).map((factor, i) => (
                    <div key={i} className={`factor-item ${factor.impact > 0 ? 'positive' : factor.impact < 0 ? 'negative' : ''}`}>
                      <span className="factor-impact">{factor.impact_label}</span>
                      <span className="factor-detail">{factor.detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk flags */}
              {detailed.risk_flags.length > 0 && (
                <div className="risk-flags">
                  <div className="flags-header">
                    <IoWarning size={14} />
                    Risk Flags
                  </div>
                  <div className="flags-list">
                    {detailed.risk_flags.map((flag, i) => (
                      <div key={i} className={`flag-item ${flag.severity}`}>
                        {flag.flag}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Data quality */}
              <div className="data-quality">
                <div className="quality-header">Data Quality</div>
                <div className="quality-bars">
                  <div className="quality-item">
                    <span>Referee</span>
                    <div className="quality-bar">
                      <div className="quality-fill" style={{ width: `${detailed.data_quality.referee_data_quality * 100}%` }} />
                    </div>
                    <span className="quality-matches">({detailed.data_quality.referee_matches} matches)</span>
                  </div>
                  <div className="quality-item">
                    <span>Teams</span>
                    <div className="quality-bar">
                      <div className="quality-fill" style={{ width: `${detailed.data_quality.team_data_quality * 100}%` }} />
                    </div>
                    <span className="quality-matches">({detailed.data_quality.team_matches} matches)</span>
                  </div>
                  <div className="quality-item">
                    <span>H2H</span>
                    <div className="quality-bar">
                      <div className="quality-fill" style={{ width: `${detailed.data_quality.h2h_data_quality * 100}%` }} />
                    </div>
                    <span className="quality-matches">({detailed.data_quality.h2h_matches} matches)</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="detail-error">Could not load detailed prediction</div>
          )}
        </div>
      )}
    </div>
  )
}

// Main page component
export function AIPredictionsPage() {
  // Track feature usage
  useFeatureTracking(FeatureNames.AI_PREDICTIONS)

  const { currentLeague } = useLeague()
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [daysAhead, setDaysAhead] = useState(7)
  const [showDerbiesOnly, setShowDerbiesOnly] = useState(false)

  // Fetch predictions
  const { data, error, isLoading, mutate } = useSWR<AIPredictionsPageResponse>(
    `/card-predictions/page/${currentLeague.api_league_id}?days_ahead=${daysAhead}${showDerbiesOnly ? '&derby_only=true' : ''}`,
    fetcher,
    { refreshInterval: 60000 } // Refresh every minute
  )

  const handleToggle = (fixtureId: number) => {
    setExpandedId(prev => prev === fixtureId ? null : fixtureId)
  }

  // Group fixtures by date
  const groupedFixtures = useMemo(() => {
    if (!data?.fixtures) return []

    const groups: { date: string; label: string; fixtures: FixtureWithPrediction[] }[] = []
    let currentDate = ''

    for (const fixture of data.fixtures) {
      const dateKey = getDateKey(fixture.kickoff_utc)
      if (dateKey !== currentDate) {
        currentDate = dateKey
        groups.push({
          date: dateKey,
          label: fmtDateHeader(fixture.kickoff_utc),
          fixtures: [],
        })
      }
      groups[groups.length - 1].fixtures.push(fixture)
    }

    return groups
  }, [data?.fixtures])

  return (
    <div className="ai-predictions-page">
      <div className="card">
        <div className="card-inner">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-main">
              <h1>
                <IoFootball size={24} style={{ marginRight: 8 }} />
                AI Card Predictions
              </h1>
              <button
                className="refresh-btn"
                onClick={() => mutate()}
                disabled={isLoading}
                title="Refresh"
              >
                <IoRefresh size={18} className={isLoading ? 'spinning' : ''} />
              </button>
            </div>
            <p className="page-subtitle">
              AI-powered card predictions using 50+ factors including referee strictness, team history, H2H patterns, and match context
            </p>
          </div>

          {/* Filters */}
          <div className="predictions-filters">
            <div className="filter-group">
              <label>Days ahead</label>
              <select value={daysAhead} onChange={e => setDaysAhead(Number(e.target.value))}>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
              </select>
            </div>
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={showDerbiesOnly}
                onChange={e => setShowDerbiesOnly(e.target.checked)}
              />
              <span>Derbies only</span>
            </label>
          </div>

          {/* Summary stats */}
          {data && (
            <div className="predictions-summary">
              <div className="summary-stat">
                <span className="stat-value">{data.fixtures_with_predictions}</span>
                <span className="stat-label">Fixtures with predictions</span>
              </div>
              <div className="summary-stat">
                <span className="stat-value">{data.league_average_cards.toFixed(1)}</span>
                <span className="stat-label">League avg cards</span>
              </div>
              {data.highest_expected?.prediction && (
                <div className="summary-stat highlight">
                  <span className="stat-value">{data.highest_expected.prediction.expected_total.toFixed(1)}</span>
                  <span className="stat-label">Highest expected</span>
                  <span className="stat-fixture">
                    {data.highest_expected.home_team_name} vs {data.highest_expected.away_team_name}
                  </span>
                </div>
              )}
              <div className="summary-stat">
                <span className="stat-value">{data.derby_fixtures.length}</span>
                <span className="stat-label">Derbies</span>
              </div>
            </div>
          )}

          {/* Loading state */}
          {isLoading && !data && (
            <div className="skeleton-loading-container">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="error-state">
              <p>Error loading predictions: {error instanceof Error ? error.message : String(error)}</p>
              <button onClick={() => mutate()}>Retry</button>
            </div>
          )}

          {/* Fixtures list */}
          {!error && data && (
            <div className="predictions-list">
              {groupedFixtures.map(group => (
                <div key={group.date} className="predictions-group">
                  <div className="group-header">{group.label}</div>
                  {group.fixtures.map(fixture => (
                    <PredictionCard
                      key={fixture.fixture_id}
                      fixture={fixture}
                      isExpanded={expandedId === fixture.fixture_id}
                      onToggle={() => handleToggle(fixture.fixture_id)}
                      leagueAvg={data.league_average_cards}
                    />
                  ))}
                </div>
              ))}

              {groupedFixtures.length === 0 && (
                <div className="empty-state">
                  <p>No fixtures found for the selected filters.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
